// src/pages/Login.js
import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";
import { adminLogin } from "../services/api";
import { saveToken } from "../utils/auth";

/**
 * Professional crypto admin login page
 * Fixes:
 *  - Shows friendly error messages (401 -> "Invalid email or password")
 *  - Prevents implicit redirects by handling HTTP statuses locally
 *  - Keeps loading/error states consistent and accessible
 */

export default function Login() {
  const [email, setEmail] = useState("admin@gmail.com");
  const [password, setPassword] = useState("admin@123");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Clear previous error and set loading
    setError("");
    setLoading(true);

    try {
      // adminLogin should return an object like { status, data }
      const res = await adminLogin({ email, password });

      // if we get a non-2xx status, handle gracefully
      if (res.status === 200) {
        // normalize token extraction
        const token =
          res?.data?.accessToken ||
          res?.data?.token ||
          res?.data?.data?.token ||
          res?.data;

        if (!token) {
          setError("Login succeeded but no token was returned.");
          return;
        }

        saveToken(token, { remember });
        navigate("/"); // success
      } else if (res.status === 401) {
        // Friendly message for authorization failure
        setError("Invalid email or password. Please try again.");
      } else if (res.status === 429) {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else if (res.status >= 500) {
        setError("Server error. Please try again later.");
      } else {
        // Fallback message using any message returned by the server
        const message =
          res?.data?.message || res?.data?.error || "Login failed. Please try again.";
        setError(message);
      }
    } catch (err) {
      // This handles network failures or unexpected exceptions
      console.error("Login error:", err);
      if (err?.message === "Network Error") {
        setError("Network error. Check your connection and try again.");
      } else {
        setError(err?.message || "An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background:
          "radial-gradient(1200px 600px at 10% 10%, rgba(99,102,241,0.06), transparent), radial-gradient(1000px 500px at 90% 90%, rgba(16,185,129,0.03), transparent)",
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            p: 3,
            bgcolor: "rgba(8,10,12,0.72)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.04)",
            boxShadow: "0 10px 30px rgba(2,6,23,0.6), inset 0 1px 0 rgba(255,255,255,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48, boxShadow: "0 6px 18px rgba(0,0,0,0.6)" }}>
              <StarIcon />
            </Avatar>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Rockstar Admin
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />
            <IconButton aria-label="admin sign in" size="small" sx={{ color: "text.secondary" }}>
              <LockOutlined />
            </IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              name="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              InputProps={{ sx: { color: "text.primary" } }}
              disabled={loading}
            />

            <TextField
              label="Password"
              name="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              disabled={loading}
              InputProps={{
                sx: { color: "text.primary" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} color="primary" size="small" disabled={loading} />}
                label={<Typography variant="body2" sx={{ color: "text.secondary" }}>Remember me</Typography>}
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => navigate("/forgot-password")}
                sx={{ color: "text.secondary" }}
                disabled={loading}
              >
                Forgot password?
              </Link>
            </Box>

            {error && (
              <Typography role="alert" color="error" sx={{ mt: 1, mb: 0.5 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2, mb: 1, py: 1.25, fontWeight: 700, textTransform: "none" }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Sign in"}
            </Button>

            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Need help?{" "}
                <Link component="button" variant="caption" onClick={() => navigate("/support")} sx={{ color: "primary.main", textDecoration: "none" }}>
                  Contact Support
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            &copy; {new Date().getFullYear()} Rockstar Bot Trading — Admin Panel
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
